import { useState, useEffect, useMemo } from 'react'
import cx from 'classnames'
import { Typography, FormControlLabel, Radio, Switch } from '@material-ui/core'

import TimeInput from '../TimeInput/TimeInput'
import { useAlertForm } from '../AlertFormProvider'
import getAlertTimingPeriods from './getAlertTimingPeriods'
import {
  DEFAULT_FIRST_END_TIME,
  DEFAULT_FIRST_START_TIME,
  DEFAULT_SECOND_END_TIME,
  DEFAULT_SECOND_START_TIME,
  MAX_TIME,
  MIN_TIME,
  TIMING_MODES
} from './timingConstants'
import { parseTimeToMinutes } from './timingUtils'
import styles from './TimeWindow.module.scss'

// Manages the schedule and sends its values to Settings.
const TimeWindow = ({ typeCheckedBox, onTimingChange }) => {
  const {
    meta: { translate, offset, initialValues }
  } = useAlertForm()

  // Use the saved schedule when editing.
  const initialTiming = initialValues?.timing ?? {}

  const [timing, setTiming] = useState(initialTiming.mode ?? TIMING_MODES.ALL_DAY)
  const [secondOccupationHoursChecked, setSecondOccupationHoursChecked] = useState(
    initialTiming.secondOccupationHoursChecked ?? false
  )

  // TimePicker uses HH:mm strings.
  const [firstStartHour, setFirstStartHour] = useState(
    initialTiming.firstStartHour ?? DEFAULT_FIRST_START_TIME
  )
  const [firstEndHour, setFirstEndHour] = useState(
    initialTiming.firstEndHour ?? DEFAULT_FIRST_END_TIME
  )
  const [secondStartHour, setSecondStartHour] = useState(
    initialTiming.secondStartHour ?? DEFAULT_SECOND_START_TIME
  )
  const [secondEndHour, setSecondEndHour] = useState(
    initialTiming.secondEndHour ?? DEFAULT_SECOND_END_TIME
  )

  // Only one time picker can be open.
  const [activePicker, setActivePicker] = useState(null)

  const isTimeRange = timing === TIMING_MODES.TIME_SLOT

  const firstRangeDisabled = !isTimeRange
  const secondRangeDisabled = !isTimeRange || !secondOccupationHoursChecked

  const handleTimingChange = event => {
    const mode = event.target.value
    setTiming(mode)
    if (mode === TIMING_MODES.ALL_DAY) {
      setSecondOccupationHoursChecked(false)
      setFirstStartHour(DEFAULT_FIRST_START_TIME)
      setFirstEndHour(DEFAULT_FIRST_END_TIME)
      setSecondStartHour(DEFAULT_SECOND_START_TIME)
      setSecondEndHour(DEFAULT_SECOND_END_TIME)
      setActivePicker(null)
    }
  }

  const handleChangeSecondSlot = event => {
    const checked = event.target.checked
    setSecondOccupationHoursChecked(checked)
    if (!checked) setActivePicker(null)
  }

  const handleOpenPicker = pickerNumber => setActivePicker(pickerNumber)
  const handleClosePicker = () => setActivePicker(null)

  const handleFirstStartChange = nextValue => setFirstStartHour(nextValue)
  const handleFirstEndChange = nextValue => setFirstEndHour(nextValue)
  const handleSecondStartChange = nextValue => setSecondStartHour(nextValue)
  const handleSecondEndChange = nextValue => setSecondEndHour(nextValue)

  const { period1, period2, auxPeriod, auxPeriod2, auxDays } = useMemo(
    () =>
      getAlertTimingPeriods({
        firstStartHour,
        firstEndHour,
        secondStartHour,
        secondEndHour,
        typeCheckedBox,
        secondOccupationHoursChecked,
        offset
      }),
    [
      firstStartHour,
      firstEndHour,
      secondStartHour,
      secondEndHour,
      typeCheckedBox,
      secondOccupationHoursChecked,
      offset
    ]
  )

  // Send the current schedule and validation result to Settings.
  const firstStartMinutes = parseTimeToMinutes(firstStartHour)
  const firstEndMinutes = parseTimeToMinutes(firstEndHour)
  const secondStartMinutes = parseTimeToMinutes(secondStartHour)
  const secondEndMinutes = parseTimeToMinutes(secondEndHour)

  const firstSlotInvalid =
    isTimeRange &&
    (firstStartMinutes === null || firstEndMinutes === null || firstStartMinutes >= firstEndMinutes)
  const secondSlotInvalid =
    isTimeRange &&
    secondOccupationHoursChecked &&
    (secondStartMinutes === null ||
      secondEndMinutes === null ||
      secondStartMinutes >= secondEndMinutes)
  // The second slot must start after the first one ends. We keep this order
  // rule and block the slot when it does not hold.
  const slotsOverlap =
    isTimeRange &&
    secondOccupationHoursChecked &&
    !secondSlotInvalid &&
    firstEndMinutes !== null &&
    secondStartMinutes !== null &&
    secondStartMinutes <= firstEndMinutes

  // Special case of the rule above: the second slot starts at the exact time
  // the first one ends. We show a clearer message for it.
  const slotsShareBoundary = slotsOverlap && secondStartMinutes === firstEndMinutes

  const isValid = !firstSlotInvalid && !secondSlotInvalid && !slotsOverlap

  useEffect(() => {
    onTimingChange({
      isTimeRange,
      period1,
      period2,
      auxPeriod,
      auxPeriod2,
      auxDays,
      secondOccupationHoursChecked,
      isValid
    })
  }, [
    isTimeRange,
    period1,
    period2,
    auxPeriod,
    auxPeriod2,
    auxDays,
    secondOccupationHoursChecked,
    isValid,
    onTimingChange
  ])

  return (
    <div className={styles.timingSection}>
      <Typography className={styles.timingTitle}>
        {translate('addParameterAlertModal.scheduleLabel')}
      </Typography>

      {/* Choose an all-day alert or a time range. */}
      <FormControlLabel
        control={
          <Radio
            className={styles.radio}
            classes={{ checked: styles.radioChecked }}
            checked={timing === TIMING_MODES.ALL_DAY}
            value={TIMING_MODES.ALL_DAY}
            onChange={handleTimingChange}
            disableRipple
          />
        }
        label={translate('addParameterAlertModal.allDay')}
      />
      <FormControlLabel
        control={
          <Radio
            className={styles.radio}
            classes={{ checked: styles.radioChecked }}
            checked={timing === TIMING_MODES.TIME_SLOT}
            value={TIMING_MODES.TIME_SLOT}
            onChange={handleTimingChange}
            disableRipple
          />
        }
        label={translate('addParameterAlertModal.timeSlot')}
      />

      <div className={cx(styles.rangeRow, { [styles.rangeRowDisabled]: firstRangeDisabled })}>
        <Typography className={styles.rangeLabel}>
          {translate('addReportModal.firstOccupationHours')}
        </Typography>
        <div className={styles.rangeControls}>
          <TimeInput
            className={styles.timePickerContent}
            value={firstStartHour}
            onChange={handleFirstStartChange}
            open={activePicker === 1}
            onOpen={() => handleOpenPicker(1)}
            onClose={handleClosePicker}
            disabled={firstRangeDisabled}
            minTime={MIN_TIME}
            maxTime={MAX_TIME}
          />
          <span className={styles.rangeSeparator} />
          <TimeInput
            className={styles.timePickerContent}
            value={firstEndHour}
            onChange={handleFirstEndChange}
            open={activePicker === 2}
            onOpen={() => handleOpenPicker(2)}
            onClose={handleClosePicker}
            disabled={firstRangeDisabled}
            minTime={MIN_TIME}
            maxTime={MAX_TIME}
          />
        </div>
      </div>

      <div className={cx(styles.rangeRow, { [styles.rangeRowDisabled]: secondRangeDisabled })}>
        <Typography className={styles.rangeLabel}>
          {translate('addReportModal.secondOccupationHours')}
        </Typography>
        <div className={styles.rangeControls}>
          <TimeInput
            className={styles.timePickerContent}
            value={secondStartHour}
            onChange={handleSecondStartChange}
            open={activePicker === 3}
            onOpen={() => handleOpenPicker(3)}
            onClose={handleClosePicker}
            disabled={secondRangeDisabled}
            minTime={MIN_TIME}
            maxTime={MAX_TIME}
          />
          <span className={styles.rangeSeparator} />
          <TimeInput
            className={styles.timePickerContent}
            value={secondEndHour}
            onChange={handleSecondEndChange}
            open={activePicker === 4}
            onOpen={() => handleOpenPicker(4)}
            onClose={handleClosePicker}
            disabled={secondRangeDisabled}
            minTime={MIN_TIME}
            maxTime={MAX_TIME}
          />
        </div>
        <Switch
          checked={secondOccupationHoursChecked}
          onChange={handleChangeSecondSlot}
          disabled={!isTimeRange}
          name='checkSecondOccupationHours'
          color='primary'
          inputProps={{ 'aria-label': 'controlled' }}
          className={styles.switch}
        />
      </div>

      {/* Show schedule errors as the user changes the times. */}
      {firstSlotInvalid && (
        <div className={styles.error}>
          <small>{translate('validation.firstSlotInvalid')}</small>
        </div>
      )}
      {secondSlotInvalid && (
        <div className={styles.error}>
          <small>{translate('validation.secondSlotInvalid')}</small>
        </div>
      )}
      {slotsOverlap && !secondSlotInvalid && (
        <div className={styles.error}>
          <small>
            {translate(
              slotsShareBoundary ? 'validation.slotsShareBoundary' : 'validation.slotsOverlap'
            )}
          </small>
        </div>
      )}
    </div>
  )
}

export default TimeWindow
